import { useState, useMemo, ReactElement, useCallback, useEffect } from "react";
import {
    Text,
    Spinner,
    Card,
    Button,
    Flex,
    Input,
    Select
} from "@chakra-ui/react";
import {
    FindingAreasEnum,
    getFindingAreaSubmissionText,
    QuestionSchemaType,
    zodButtonOptions,
    zodQuestionTypes
} from "../utils/surveyStatusSchemas";
import { AnswerQuestionArgs, useAnswerQuestion } from "../api";
import { useAppState } from "../state/useAppState";

export type FieldChangeEvent = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    target: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type?: any;
};

interface QuestionFieldProps {
    question: QuestionSchemaType;
    responseCallback: (question: QuestionSchemaType, response: string, lastAnswerId: string) => void;
    defaultResponse: string;
    findingAreaId: FindingAreasEnum;
}

const QuestionField = ({
    question,
    responseCallback,
    defaultResponse,
    findingAreaId
} : QuestionFieldProps) => {
    const [saving, setSaving] = useState<boolean>(false);
    const [response, setResponse] = useState<string>(defaultResponse);
    const answerQuestionMutation = useAnswerQuestion();
    const surveyId = useAppState(state => state.surveyId);

    const handleSave = useCallback(
        async (value: string): Promise<void> => {
            setSaving(true);
            setResponse(value);
    
            const answerQuestionArgs: AnswerQuestionArgs = {
                surveyId: surveyId!,
                questionId: question.id,
                answerText: value,
                findingArea: getFindingAreaSubmissionText(findingAreaId!)
            };
    
            const answerQuestionResponse = await answerQuestionMutation.mutateAsync(answerQuestionArgs);
            responseCallback(question, value, answerQuestionResponse.id!);
            setSaving(false);
        },
        [question, surveyId, findingAreaId, responseCallback, answerQuestionMutation]
    );

    const savingComp = useCallback(() => {
        return saving ? (
            <>
                <Text fontSize="xs" marginRight="5">Saving...</Text>
                <Spinner size="sm" />
            </>
        ) : null;
    }, [saving]);

    const getButtonColor = useCallback((type: string) => {
        if (type === response) {
            return "solid";
        }
        return "outline";
    }, [response]);

    useEffect(() => {
        if (question.type === zodQuestionTypes.enum.DropDown && response === "") {
            handleSave(question.values?.[0] || "N/A");
        }
    }, [])

    const inputUI = useMemo<ReactElement>(() => {
        switch (question.type) {
            case zodQuestionTypes.enum.Number: 
                return (
                    <>
                        <Input type="number"
                               variant="filled"
                               marginTop="2rem"
                               defaultValue={defaultResponse}
                               onBlur={e => handleSave(e.target.value)}
                        />
                        <Flex justifyContent="right">
                            {savingComp()}
                        </Flex>
                    </>
                );
            case zodQuestionTypes.enum.FreeText:
                return (
                    <>
                        <Input
                            marginTop="2rem"
                            defaultValue={defaultResponse}
                            onBlur={(e) => handleSave(e.target.value)}
                        />
                        <Flex justifyContent="right">
                            {savingComp()}
                        </Flex>
                    </>
                );
            case zodQuestionTypes.enum.YesNo:
            case zodQuestionTypes.enum.YesNA:
                return (
                    <Flex 
                        alignItems="center" 
                        justifyContent="right" 
                        marginTop="5"
                        display="flex" 
                        gap="0.5rem"
                    >
                        {savingComp()}
                        <Button 
                            colorScheme="blue"
                            variant={getButtonColor(zodButtonOptions.enum.YES)}
                            onClick={() => {
                                handleSave(zodButtonOptions.enum.YES);
                            }}
                        >
                            Yes
                        </Button>
                        <Button 
                            colorScheme="blue" 
                            variant={getButtonColor(zodButtonOptions.enum.NO)}
                            onClick={() => {
                                handleSave(zodButtonOptions.enum.NO);
                            }}
                        >
                            No
                        </Button>
                        {question.type === zodQuestionTypes.enum.YesNA.valueOf() && (
                            <Button
                                colorScheme="blue"
                                variant={getButtonColor(zodButtonOptions.enum.NA)}
                                onClick={() => {
                                    handleSave(zodButtonOptions.enum.NA);
                                }}
                            >
                                N/A
                            </Button>
                        )}
                    </Flex>
                );
            case zodQuestionTypes.enum.YesNoDontKnow:
                return (
                    <Flex
                        alignItems="center"
                        justifyContent="right"
                        marginTop="5"
                        display="flex"
                        gap="0.5rem"
                    >
                        {savingComp()}
                        <Button
                            colorScheme="blue"
                            variant={getButtonColor(zodButtonOptions.enum.YES)}
                            onClick={() => {
                                handleSave(zodButtonOptions.enum.YES);
                            }}
                        >
                            Yes
                        </Button>
                        <Button
                            colorScheme="blue"
                            variant={getButtonColor(zodButtonOptions.enum.NO)}
                            onClick={() => {
                                handleSave(zodButtonOptions.enum.NO);
                            }}
                        >
                            No
                        </Button>
                        <Button
                            colorScheme="blue"
                            variant={getButtonColor(zodButtonOptions.enum.DONT_KNOW)}
                            onClick={() => {
                                handleSave(zodButtonOptions.enum.DONT_KNOW);
                            }}
                        >
                            Don't Know
                        </Button>
                    </Flex>
                );
            case zodQuestionTypes.enum.DropDown:
                return (
                    <>
                        <Select
                            value={response}
                            onChange={e => {
                                setResponse(e.target.value);
                                handleSave(e.target.value);
                            }}
                        >
                            {question.values?.map((value) => (
                                <option key={value} value={value}>
                                    {value}
                                </option>
                            ))}
                        </Select>
                        <Flex justifyContent="right" pt="1rem">
                            {savingComp()}
                        </Flex>
                    </>
                )
            default:
                return (
                    <></>
                );
        }
    }, [getButtonColor, handleSave, question.type, savingComp]);

    return (
        <Card padding="1.5rem">
            <Text>{question.text}</Text>
            {inputUI}
        </Card>
    );
}

export default QuestionField;